import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Loader2, CheckCircle2, AlertTriangle, Sparkles, Wheat, Wrench, Scale, Home, Utensils, Dumbbell, ShoppingBag, Calendar, CalendarRange, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

const ICON_MAP = { Wheat, Wrench, Scale, Home, Utensils, Dumbbell };

export default function SectorPresetsAdmin() {
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState({ reset_first: false, apply_theme: true, seed_content: true });
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/template/sectors")
      .then(r => setSectors(r.data.sectors || []))
      .catch(() => toast.error("Impossible de charger les presets"))
      .finally(() => setLoading(false));
  }, []);

  const apply = async () => {
    if (!selected) return;
    if (options.reset_first && !window.confirm("⚠️ RESET : toutes les données actuelles (services, produits, blog, FAQ, zones, réservations location) seront effacées avant d'appliquer le preset. Continuer ?")) return;
    setApplying(true);
    setResult(null);
    try {
      const { data } = await api.post("/template/apply-sector", {
        sector_id: selected,
        reset_first: options.reset_first,
        apply_theme: options.apply_theme,
        seed_content: options.seed_content,
      });
      setResult(data.applied);
      toast.success("Preset secteur appliqué !");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'application");
    } finally { setApplying(false); }
  };

  if (loading) return <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto"/></div>;

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="sector-presets-admin">
      <header>
        <h1 className="font-display text-3xl font-black text-[color:var(--brand-navy)]">Preset secteur</h1>
        <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Sélectionnez un secteur pour pré-remplir votre site avec du contenu adapté (services, blog, FAQ, thème, modules activés).</p>
      </header>

      {/* Sector grid */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {sectors.map(s => {
          const Icon = ICON_MAP[s.icon] || Sparkles;
          const isActive = selected === s.id;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => setSelected(s.id)}
              className={`text-left p-5 rounded-xl border-2 transition-all ${isActive ? "border-[color:var(--brand-sky)] bg-[color:var(--brand-sky)]/5 shadow-md" : "border-[hsl(var(--border))] hover:border-[color:var(--brand-sky)]/40 hover:shadow-sm"}`}
              data-testid={`sector-card-${s.id}`}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${s.color}22`, color: s.color }}>
                  <Icon className="w-5 h-5"/>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-display font-bold text-[color:var(--brand-navy)]">{s.label}</h3>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{s.description}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">
                    {s.has_shop && <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-50 text-orange-700 border border-orange-200 flex items-center gap-0.5"><ShoppingBag className="w-2.5 h-2.5"/>Boutique</span>}
                    {s.has_booking && <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 border border-cyan-200 flex items-center gap-0.5"><Calendar className="w-2.5 h-2.5"/>Réservation</span>}
                    {s.has_rental && <span className="text-[10px] px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 flex items-center gap-0.5"><CalendarRange className="w-2.5 h-2.5"/>Location</span>}
                  </div>
                </div>
                {isActive && <CheckCircle2 className="w-5 h-5 text-[color:var(--brand-sky)] shrink-0"/>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Options */}
      {selected && (
        <Card className="p-5" data-testid="sector-options">
          <h2 className="font-display text-lg font-bold text-[color:var(--brand-navy)] mb-3">Options d'application</h2>
          <div className="space-y-3">
            <label className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] cursor-pointer">
              <div>
                <Label className="font-semibold cursor-pointer">Seed du contenu</Label>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Insère les services, blog, FAQ, produits du preset (idempotent — ne duplique pas).</p>
              </div>
              <Switch checked={options.seed_content} onCheckedChange={v => setOptions({...options, seed_content: v})} data-testid="opt-seed"/>
            </label>
            <label className="flex items-center justify-between p-3 rounded-lg border border-[hsl(var(--border))] cursor-pointer">
              <div>
                <Label className="font-semibold cursor-pointer">Appliquer le thème</Label>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Active le thème visuel recommandé pour ce secteur.</p>
              </div>
              <Switch checked={options.apply_theme} onCheckedChange={v => setOptions({...options, apply_theme: v})} data-testid="opt-theme"/>
            </label>
            <label className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50 cursor-pointer">
              <div>
                <Label className="font-semibold cursor-pointer text-red-900 flex items-center gap-1"><AlertTriangle className="w-4 h-4"/>Reset complet avant</Label>
                <p className="text-xs text-red-700">⚠️ Efface toutes les données actuelles (services, produits, blog, réservations) avant d'appliquer. Usage nouveau site uniquement.</p>
              </div>
              <Switch checked={options.reset_first} onCheckedChange={v => setOptions({...options, reset_first: v})} data-testid="opt-reset"/>
            </label>
          </div>
          <Button className="mt-5 w-full" size="lg" onClick={apply} disabled={applying} data-testid="apply-sector-btn">
            {applying ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Sparkles className="w-4 h-4 mr-2"/>}
            Appliquer le preset "{sectors.find(s => s.id === selected)?.label}"
            <ArrowRight className="w-4 h-4 ml-2"/>
          </Button>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="p-5 bg-emerald-50 border-emerald-200" data-testid="sector-result">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0 mt-0.5"/>
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold text-emerald-900">Preset "{result.label}" appliqué</h3>
              <div className="text-sm text-emerald-800 mt-2 space-y-1">
                <div>✓ {result.settings_patched?.length || 0} champs de paramètres mis à jour</div>
                {result.theme_applied && <div>✓ Thème appliqué : <code className="text-xs bg-white px-1.5 py-0.5 rounded">{result.theme_applied}</code></div>}
                {result.seeded && (
                  <div>✓ Contenu inséré :
                    <ul className="list-disc list-inside ml-2 text-xs">
                      {Object.entries(result.seeded).filter(([_,v]) => v > 0).map(([k,v]) => <li key={k}>{v} {k}</li>)}
                    </ul>
                  </div>
                )}
                {result.wiped && <div className="text-xs text-red-700 mt-1">⚠ {result.wiped.length} collections effacées (reset complet)</div>}
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" asChild><a href="/" target="_blank" rel="noopener">Voir le site public</a></Button>
                <Button variant="outline" size="sm" asChild><a href="/admin/settings">Paramètres</a></Button>
                <Button variant="outline" size="sm" asChild><a href="/admin/services">Prestations</a></Button>
              </div>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
